import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { RootState } from '../store';
import { Agreement } from '../../models/agreement';
import {
  UserAgreement,
  PendingUserAgreement,
} from '../../models/user-agreement';

interface AgreementsState {
  agreements: Agreement[];
  userAgreements: UserAgreement[];
  pendingUserAgreements: PendingUserAgreement[];
  isLoading: boolean;
  userAgreementsLoading: boolean;
  agreementsLoading: boolean;
  agreementsFetched: boolean;
  userAgreementsFetched: boolean;
  error: string | null;
  checkingLatestUserAgreement: boolean;
  latestUserAgreementAccepted: boolean | null;
}

const initialState: AgreementsState = {
  agreements: [],
  userAgreements: [],
  pendingUserAgreements: [],
  isLoading: false,
  userAgreementsLoading: false,
  agreementsLoading: false,
  agreementsFetched: false,
  userAgreementsFetched: false,
  error: null,
  checkingLatestUserAgreement: false,
  latestUserAgreementAccepted: null,
};

export const fetchLatestUserAgreementAccepted = createAsyncThunk(
  'agreements/fetchLatestUserAgreementAccepted',
  async (csrService: AxiosInstance, { getState }) => {
    const state = getState() as RootState;
    const authToken = state.auth.auth.access.access;

    const response = await csrService.get(
      '/api/v1/users/agreements/latest-accepted',
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    );

    return response.data as boolean;
  },
);

export const fetchAllAgreements = createAsyncThunk(
  'agreements/fetchAll',
  async (csrService: AxiosInstance, { rejectWithValue }) => {
    const response = await csrService.get('/api/v1/agreements');
    return response.data as Agreement[];
  },
);

export const fetchUserAgreements = createAsyncThunk(
  'agreements/fetchUser',
  async (csrService: AxiosInstance, { getState }) => {
    const state = getState() as RootState;

    const response = await csrService.get('/api/v1/users/agreements');

    return response.data as UserAgreement[];
  },
);

export const publishUserAgreement = createAsyncThunk(
  'agreements/createUserAgreement',
  async (
    {
      csrService,
      agreementId,
    }: { csrService: AxiosInstance; agreementId: number },
    { getState },
  ) => {
    const state = getState() as RootState;
    const authToken = state.auth.auth.access.access;

    const response = await csrService.post('/api/v1/users/agreements', {
      agreementId,
    });
    return response.data as UserAgreement;
  },
);

const agreementSlice = createSlice({
  name: 'agreements',
  initialState,
  reducers: {
    setAgreements(state, action: PayloadAction<Agreement[]>) {
      state.agreements = action.payload;
    },
    // This reducer updates the user agreements state
    setUserAgreements(state, action: PayloadAction<UserAgreement[]>) {
      state.userAgreements = action.payload;
    },
    // This reducer can be used to set an error if any API calls fail
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setPendingAgreement(state, action: PayloadAction<PendingUserAgreement>) {
      state.pendingUserAgreements.push(action.payload);
    },
    removePendingAgreement(state, action: PayloadAction<number>) {
      state.pendingUserAgreements = state.pendingUserAgreements.filter(
        (agreement) => agreement.agreementId !== action.payload,
      );
    },
    clearPendingAgreements(state) {
      state.pendingUserAgreements = [];
    },
    clearUserAgreements(state) {
      state.userAgreements = [];
      state.userAgreementsFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAgreements.pending, (state) => {
        state.agreementsLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAllAgreements.fulfilled,
        (state, action: PayloadAction<Agreement[]>) => {
          state.agreementsLoading = false;
          state.agreementsFetched = true;
          state.agreements = action.payload;
          state.error = null;
        },
      )
      .addCase(fetchAllAgreements.rejected, (state, action) => {
        state.agreementsLoading = false;
        state.error = action.error.message || 'Failed to fetch agreements.';
      });

    builder
      .addCase(fetchUserAgreements.pending, (state) => {
        state.userAgreementsLoading = true;
        state.error = null;
      })
      .addCase(
        fetchUserAgreements.fulfilled,
        (state, action: PayloadAction<UserAgreement[]>) => {
          state.userAgreementsLoading = false;
          state.userAgreementsFetched = true;
          state.userAgreements = action.payload;
          state.error = null;
        },
      )
      .addCase(fetchUserAgreements.rejected, (state, action) => {
        state.userAgreementsLoading = false;
        state.error =
          action.error.message || 'Failed to fetch user agreements.';
      });

    builder
      .addCase(publishUserAgreement.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        publishUserAgreement.fulfilled,
        (state, action: PayloadAction<UserAgreement>) => {
          state.isLoading = false;
          state.userAgreements.push(action.payload);
          state.error = null;
        },
      )
      .addCase(publishUserAgreement.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || 'Failed to create user agreement.';
      });

    builder
      .addCase(fetchLatestUserAgreementAccepted.pending, (state) => {
        state.checkingLatestUserAgreement = true;
        state.error = null;
      })
      .addCase(
        fetchLatestUserAgreementAccepted.fulfilled,
        (state, action: PayloadAction<boolean>) => {
          state.checkingLatestUserAgreement = false;
          state.latestUserAgreementAccepted = action.payload;
        },
      )
      .addCase(fetchLatestUserAgreementAccepted.rejected, (state, action) => {
        state.checkingLatestUserAgreement = false;
        state.latestUserAgreementAccepted = false;
        state.error =
          action.error.message || 'Failed to fetch latest user agreement.';
      });
  },
});

export const {
  setAgreements,
  setUserAgreements,
  setPendingAgreement,
  removePendingAgreement,
  clearPendingAgreements,
  clearUserAgreements,
  setError,
} = agreementSlice.actions;

export default agreementSlice.reducer;
